import _ from 'underscore';
import BaseExporter from './BaseExporter';
import TimecodeConverter from '../utils/TimecodeConverter';
import ti18n from 'i18n!nls/ExportPopupi18n';

/**
 * EdlExporter - Export timeline data to EDL formats
 * 
 * Supports:
 * - CMX 3600 EDL format (industry standard)
 * - Samplitude EDL format (Reaper compatible)
 * 
 * Features:
 * - Multi-track organization by source file
 * - Frame-accurate timing
 * - Proper reel name handling
 * - Source file path references
 */
const EdlExporter = BaseExporter.extend({

    /**
     * Export timeline data as EDL format
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} EDL content as text
     */
    export: function(timelineData, options) {
        options = this.validateAndExtendOptions(options);
        this.validateTimelineData(timelineData);

        // Determine which EDL format to use
        if (options.edlFormat === 'samplitude') {
            return this.exportSamplitudeEDL(timelineData, options);
        } else if (options.edlFormat === 'reaper') {
            return this.exportReaperEDL(timelineData, options);
        } else {
            return this.exportCMX3600EDL(timelineData, options);
        }
    },

    /**
     * Export timeline data as CMX 3600 EDL format
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} CMX 3600 EDL content as text
     */
    exportCMX3600EDL: function(timelineData, options) {
        try {
            // Generate proper CMX 3600 EDL header
            let edlContent = `TITLE: ${options.projectTitle}\n`;
            
            // Set FCM (Frame Count Mode) based on frame rate
            if (TimecodeConverter.shouldUseDropFrame(options.frameRate)) {
                edlContent += `FCM: DROP FRAME\n`;
            } else {
                edlContent += `FCM: NON-DROP FRAME\n`;
            }
            
            edlContent += '\n';

            // Create track assignment - each unique source file gets its own track
            const tracksBySource = this.createTrackAssignmentMap(timelineData, options);

            // Generate EDL events
            timelineData.forEach((clip, index) => {
                const eventNumber = (index + 1).toString().padStart(3, '0');
                const reelName = (clip.reelName || options.reelNamePrefix).padEnd(8).substring(0, 8);
                const trackType = clip.hasVideo ? 'V' : 'A';
                const clipFileName = this.getClipFileName(clip);
                
                // Assign track number based on source file
                const trackNumber = tracksBySource.get(this.getClipMediaKey(clip)) || 1;
                const track = trackType + trackNumber.toString();
                const transitionType = 'C'; // Cut transition
                
                // Convert seconds to timecode
                const recordIn = TimecodeConverter.convertSecondsToTimecode(
                    clip.startTimeInSeconds, 
                    options.frameRate,
                    TimecodeConverter.shouldUseDropFrame(options.frameRate)
                );
                const recordOut = TimecodeConverter.convertSecondsToTimecode(
                    clip.endTimeInSeconds, 
                    options.frameRate,
                    TimecodeConverter.shouldUseDropFrame(options.frameRate)
                );
                const sourceIn = clip.sourceOffsetTimecode || TimecodeConverter.convertSecondsToTimecode(
                    clip.sourceStartInSeconds, 
                    options.frameRate,
                    TimecodeConverter.shouldUseDropFrame(options.frameRate)
                );
                const sourceOut = TimecodeConverter.convertSecondsToTimecode(
                    clip.sourceEndInSeconds, 
                    options.frameRate,
                    TimecodeConverter.shouldUseDropFrame(options.frameRate)
                );

                // CMX 3600 EDL format: EVENT  REEL     TRACK  TRANS   SOURCE_IN    SOURCE_OUT   RECORD_IN    RECORD_OUT
                edlContent += `${eventNumber}  ${reelName} ${track.padEnd(6)} ${transitionType}        ${sourceIn} ${sourceOut} ${recordIn} ${recordOut}\n`;
                
                // Add source file comment with appropriate path (optional but helpful)
                if (clipFileName) {
                    const sourceFilePath = this.getMediaFilePath(clip, options);
                    edlContent += `* FROM CLIP NAME: ${clipFileName}\n`;
                    edlContent += `* SOURCE FILE: ${sourceFilePath}\n`;
                    edlContent += `* TRACK: ${trackType}${trackNumber} (${clipFileName})\n`;
                }
                
                edlContent += '\n';
            });

            return edlContent;

        } catch (error) {
            console.error('Error generating CMX 3600 EDL:', error);
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), error);
        }
    },

    /**
     * Export timeline data as Samplitude EDL format (compatible with Reaper)
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Samplitude EDL content as text
     */
    exportSamplitudeEDL: function(timelineData, options) {
        try {
            // Default sample rate (can be overridden in options)
            const sampleRate = options.sampleRate || 48000;
            const channels = options.outputChannels || 2;
            
            // Generate Samplitude EDL header
            let edlContent = `Samplitude EDL File Format Version 1.5\n`;
            edlContent += `Title: "${options.projectTitle}"\n`;
            edlContent += `Sample Rate: ${sampleRate}\n`;
            edlContent += `Output Channels: ${channels}\n`;
            edlContent += `\n\n`;

            // Create source table - list all unique source files
            const uniqueSources = new Map();
            let sourceIndex = 1;
            
            timelineData.forEach(clip => {
                const mediaKey = this.getClipMediaKey(clip);
                if (mediaKey && !uniqueSources.has(mediaKey)) {
                    uniqueSources.set(mediaKey, {
                        sourceIndex: sourceIndex,
                        fileName: this.getClipFileName(clip),
                        sourceFilePath: this.getMediaFilePath(clip, options)
                    });
                    sourceIndex++;
                }
            });

            // Write source table
            edlContent += `Source Table Entries: ${uniqueSources.size}\n`;
            for (const sourceInfo of uniqueSources.values()) {
                edlContent += `   ${sourceInfo.sourceIndex} "${sourceInfo.sourceFilePath}"\n`;
            }
            edlContent += `\n`;

            // Generate track headers and entries
            const trackGroups = this.createTrackGroups(timelineData, options);
            trackGroups.forEach(trackGroup => {
                const fileName = trackGroup.trackName;
                const trackNumber = trackGroup.trackNumber;
                const trackName = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension for track name
                edlContent += `Track ${trackNumber}: "${trackName}" Solo: 0 Mute: 0\n`;
                
                // Generate track entries for clips from this source
                trackGroup.clips.forEach(clip => {
                    const sourceInfo = uniqueSources.get(this.getClipMediaKey(clip));
                    const sourceId = sourceInfo ? sourceInfo.sourceIndex : 1;
                    
                    // Convert times to samples
                    const playIn = Math.round(clip.startTimeInSeconds * sampleRate);
                    const playOut = Math.round(clip.endTimeInSeconds * sampleRate);
                    const recordIn = Math.round(clip.sourceStartInSeconds * sampleRate);
                    const recordOut = Math.round(clip.sourceEndInSeconds * sampleRate);
                    
                    // Volume in dB (default 0.0)
                    const volume = clip.volume !== undefined ? this.convertLinearToDb(clip.volume) : 0.0;
                    
                    // Mute and lock flags
                    const mute = clip.muted ? '1' : '0';
                    const lock = clip.locked ? '1' : '0';
                    
                    // Fade settings (defaults)
                    const fadeIn = 0;
                    const fadeInPercent = 0;
                    const fadeInCurve = '"*default"';
                    const fadeOut = 0;
                    const fadeOutPercent = 0;
                    const fadeOutCurve = '"*default"';
                    
                    // Clip name
                    const clipName = clip.clipName || this.getClipFileName(clip) || 'clip';
                    
                    // Samplitude EDL line format:
                    // Source Track Play-In Play-Out Record-In Record-Out Vol(dB) MT LK FadeIn % CurveType FadeOut % CurveType Name
                    edlContent += `${sourceId}  ${trackNumber}  ${playIn}  ${playOut}  ${recordIn}  ${recordOut}  ${volume.toFixed(1)}  ${mute}  ${lock}  ${fadeIn}  ${fadeInPercent}  ${fadeInCurve}  ${fadeOut}  ${fadeOutPercent}  ${fadeOutCurve}  "${clipName}"\n`;
                });
                
                edlContent += `\n`;
            });

            // Add track volume and pan settings for each track
            trackGroups.forEach(trackGroup => {
                edlContent += `Volume for Track ${trackGroup.trackNumber}:\n`;
                edlContent += `   0 0.000\n`;
                edlContent += `\n`;
                edlContent += `Pan for Track ${trackGroup.trackNumber}:\n`;
                edlContent += `   0 1.00000\n`;
                edlContent += `\n`;
            });

            return edlContent;

        } catch (error) {
            console.error('Error generating Samplitude EDL:', error);
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), error);
        }
    },

    /**
     * Export timeline data as Reaper EDL format (Reaper-specific)
     * 
     * @param {Array} timelineData - Array of timeline clip objects
     * @param {Object} options - Export options
     * @returns {String} Reaper EDL content as text
     */
    exportReaperEDL: function(timelineData, options) {
        try {
            // Default sample rate (can be overridden in options)
            const sampleRate = options.sampleRate || 48000;
            const channels = options.outputChannels || 2;
            
            // Generate Reaper EDL header (more specific format)
            let edlContent = `Samplitude EDL File Format Version 1.5\n`;
            edlContent += `Title: "${options.projectTitle}"\n`;
            edlContent += `Sample Rate: ${sampleRate}\n`;
            edlContent += `Output Channels: ${channels}\n`;
            edlContent += `\n\n`;

            // Create source table - list all unique source files
            const uniqueSources = new Map();
            let sourceIndex = 1;
            
            timelineData.forEach(clip => {
                const mediaKey = this.getClipMediaKey(clip);
                if (mediaKey && !uniqueSources.has(mediaKey)) {
                    const fileName = this.getClipFileName(clip);
                    uniqueSources.set(mediaKey, {
                        sourceIndex: sourceIndex,
                        fileName: fileName,
                        sourceFilePath: `media/${fileName}`
                    });
                    sourceIndex++;
                }
            });

            // Write source table
            edlContent += `Source Table Entries: ${uniqueSources.size}\n`;
            for (const sourceInfo of uniqueSources.values()) {
                edlContent += `   ${sourceInfo.sourceIndex} "${sourceInfo.sourceFilePath}"\n`;
            }
            edlContent += `\n`;

            // Generate track headers and entries
            const trackGroups = this.createTrackGroups(timelineData, options);
            trackGroups.forEach(trackGroup => {
                const fileName = trackGroup.trackName;
                const trackNumber = trackGroup.trackNumber;
                const trackName = fileName.replace(/\.[^/.]+$/, ""); // Remove file extension for track name
                edlContent += `Track ${trackNumber}: "${trackName}" Solo: 0 Mute: 0\n`;
                
                // Generate track entries for clips from this source
                trackGroup.clips.forEach(clip => {
                    const sourceInfo = uniqueSources.get(this.getClipMediaKey(clip));
                    const sourceId = sourceInfo ? sourceInfo.sourceIndex : 1;
                    
                    // Convert times to samples (Reaper expects integer sample values)
                    const playIn = Math.round(clip.startTimeInSeconds * sampleRate);
                    const playOut = Math.round(clip.endTimeInSeconds * sampleRate);
                    const recordIn = Math.round(clip.sourceStartInSeconds * sampleRate);
                    const recordOut = Math.round(clip.sourceEndInSeconds * sampleRate);
                    
                    // Volume in dB (default 0.0)
                    const volume = clip.volume !== undefined ? this.convertLinearToDb(clip.volume) : 0.0;
                    
                    // Mute and lock flags
                    const mute = clip.muted ? '1' : '0';
                    const lock = clip.locked ? '1' : '0';
                    
                    // Fade settings (defaults)
                    const fadeIn = 0;
                    const fadeInPercent = 0;
                    const fadeInCurve = '"*default"';
                    const fadeOut = 0;
                    const fadeOutPercent = 0;
                    const fadeOutCurve = '"*default"';
                    
                    // Clip name
                    const clipName = clip.clipName || this.getClipFileName(clip) || 'clip';
                    
                    // Reaper EDL line format (ensure exact spacing):
                    // Source Track Play-In Play-Out Record-In Record-Out Vol(dB) MT LK FadeIn % CurveType FadeOut % CurveType Name
                    edlContent += `${sourceId}  ${trackNumber}  ${playIn}  ${playOut}  ${recordIn}  ${recordOut}  ${volume.toFixed(1)}  ${mute}  ${lock}  ${fadeIn}  ${fadeInPercent}  ${fadeInCurve}  ${fadeOut}  ${fadeOutPercent}  ${fadeOutCurve}  "${clipName}"\n`;
                });
                
                edlContent += `\n`;
            });

            // Add track volume and pan settings for each track
            trackGroups.forEach(trackGroup => {
                edlContent += `Volume for Track ${trackGroup.trackNumber}:\n`;
                edlContent += `   0 0.000\n`;
                edlContent += `\n`;
                edlContent += `Pan for Track ${trackGroup.trackNumber}:\n`;
                edlContent += `   0 1.00000\n`;
                edlContent += `\n`;
            });

            return edlContent;

        } catch (error) {
            console.error('Error generating Reaper EDL:', error);
            throw this.createExportError(window.getI18n(ti18n, 'EXPORT_FAILED'), error);
        }
    },

    /**
     * Get supported EDL formats
     * @returns {Array} Array of supported format objects
     */
    getSupportedFormats: function() {
        return [
            {
                id: 'cmx3600',
                name: 'CMX 3600 EDL',
                description: 'Industry standard EDL format',
                extension: '.edl',
                supports: ['video', 'audio', 'timecode', 'tracks']
            },
            {
                id: 'samplitude',
                name: 'Samplitude EDL',
                description: 'Samplitude native EDL format',
                extension: '.edl',
                supports: ['audio', 'samples', 'tracks', 'volume', 'fade']
            },
            {
                id: 'reaper',
                name: 'Reaper EDL',
                description: 'Reaper-specific EDL format',
                extension: '.edl',
                supports: ['audio', 'samples', 'tracks', 'volume', 'fade']
            }
        ];
    },

    /**
     * Validate EDL-specific options
     * @param {Object} options - Export options
     * @returns {Object} Validated options
     */
    validateAndExtendOptions: function(options) {
        const validatedOptions = BaseExporter.prototype.validateAndExtendOptions.call(this, options);
        
        // Validate EDL format
        if (!validatedOptions.edlFormat || 
            !['cmx3600', 'samplitude', 'reaper'].includes(validatedOptions.edlFormat)) {
            validatedOptions.edlFormat = 'cmx3600'; // Default to CMX 3600
        }
        
        // Validate sample rate for Samplitude and Reaper
        if (validatedOptions.edlFormat === 'samplitude' || validatedOptions.edlFormat === 'reaper') {
            if (!validatedOptions.sampleRate || validatedOptions.sampleRate <= 0) {
                validatedOptions.sampleRate = 48000;
            }
            if (!validatedOptions.outputChannels || validatedOptions.outputChannels <= 0) {
                validatedOptions.outputChannels = 2;
            }
        }
        
        // Validate reel name prefix
        if (!validatedOptions.reelNamePrefix) {
            validatedOptions.reelNamePrefix = 'V';
        }
        
        return validatedOptions;
    },

    /**
     * Get format-specific default options
     * @param {String} format - EDL format ('cmx3600' or 'samplitude')
     * @returns {Object} Format-specific options
     */
    getFormatDefaults: function(format) {
        const baseDefaults = this.getDefaultOptions();
        
        if (format === 'samplitude') {
            return _.extend(baseDefaults, {
                edlFormat: 'samplitude',
                sampleRate: 48000,
                outputChannels: 2,
                mediaPathType: 'relative' // Samplitude works better with relative paths
            });
        } else {
            return _.extend(baseDefaults, {
                edlFormat: 'cmx3600',
                frameRate: 25,
                reelNamePrefix: 'V'
            });
        }
    }

});

export default EdlExporter;
